import { ChevronDown, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../../lib/utils";
import { BatchCard } from "./batch-card";
import { BOARD_COLUMNS, STATUS_META, type Batch, type BatchStatus } from "./types";

export function KanbanBoard({
  batches,
  onSelect,
  onMenu,
  onShowAll,
  onQuickAdd,
  columns = BOARD_COLUMNS,
  columnStatuses,
}: {
  batches: Batch[];
  onSelect: (b: Batch) => void;
  onMenu: (b: Batch, action: string) => void;
  onShowAll: (status: string) => void;
  onQuickAdd: (status: string) => void;
  columns?: { id: BatchStatus; label: string }[];
  columnStatuses?: Partial<Record<BatchStatus, BatchStatus[]>>;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [sortByProgress, setSortByProgress] = useState(false);

  const byCol = useMemo(() => {
    const map: Record<string, Batch[]> = {};
    for (const col of columns) {
      const statuses = columnStatuses?.[col.id] ?? [col.id];
      const items = batches.filter((b) => statuses.includes(b.status));
      map[col.id] = sortByProgress
        ? [...items].sort((a, z) => (z.completed / (z.total || 1)) - (a.completed / (a.total || 1)))
        : items;
    }
    return map;
  }, [batches, sortByProgress, columns, columnStatuses]);

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="board-fade board-scroll flex h-full min-h-0 min-w-0 flex-1 gap-4 overflow-x-auto">
      {columns.map((col) => {
        const meta = STATUS_META[col.id];
        const items = byCol[col.id];
        const visible = items.slice(0, 25);                 // BR-04-03 cap
        const isCollapsed = collapsed.has(col.id);
        const callsTotal = items.reduce((s, b) => s + b.total, 0);

        return (
          <div key={col.id} className={cn("flex h-full min-h-0 shrink-0 flex-col", isCollapsed ? "w-14" : "w-[300px] min-w-[300px]")}>
            {/* column header */}
            <header
              className="shrink-0 rounded-t-xl border-t-2 bg-elevated px-3 py-2.5 shadow-sm"
              style={{ borderTopColor: meta.spine }}
            >
              {isCollapsed ? (
                <button onClick={() => toggleCollapse(col.id)} aria-label={`Expand ${col.label}`}
                  className="mx-auto flex h-6 w-6 items-center justify-center rounded-lg hover:bg-soft">
                  <ChevronDown className="h-4 w-4 -rotate-90 text-ink-3" />
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: meta.dot }} />
                  <h3 className="text-sm font-bold text-ink">{col.label}</h3>
                  <span className="rounded-full bg-soft px-2 py-0.5 font-mono text-[10px] font-bold text-ink-2">{items.length}</span>
                  {callsTotal > 0 && <span className="ml-auto font-mono text-[10px] text-ink-3" title="Total calls in column">Σ {callsTotal.toLocaleString("en-IN")}</span>}
                  <button onClick={() => onQuickAdd(col.id)} aria-label={`Add to ${col.label}`}
                    className="rounded-lg p-1 text-ink-3 transition-colors hover:bg-brand-soft hover:text-brand">
                    <Plus className="h-4 w-4" />
                  </button>
                  <button onClick={() => toggleCollapse(col.id)} aria-label={`Collapse ${col.label}`}
                    className="rounded-lg p-1 text-ink-3 hover:bg-soft">
                    <ChevronDown className="h-4 w-4 rotate-90" />
                  </button>
                </div>
              )}
            </header>

            {/* column body */}
            {!isCollapsed && (
              <div className="column-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-b-xl bg-soft/40 p-2">
                {visible.map((b) => (
                  <BatchCard key={b.schedule_id} b={b} onSelect={() => onSelect(b)} onMenu={(a) => onMenu(b, a)} />
                ))}

                {items.length > 25 && (
                  <button onClick={() => onShowAll(col.id)}
                    className="w-full rounded-lg border border-dashed border-line py-2 text-xs font-semibold text-ink-3 transition-colors hover:border-brand hover:text-brand">
                    {items.length - 25} more — open in table
                  </button>
                )}

                {items.length === 0 && (
                  <button onClick={() => onQuickAdd(col.id)}
                    className="flex min-h-[7rem] w-full flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed border-line text-xs font-semibold text-ink-3 transition-colors hover:border-brand/50 hover:bg-brand-soft/20 hover:text-brand">
                    <Plus className="mb-1 h-4 w-4" /> Drop or add batch
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* hidden global sort toggle lives in the page toolbar; board reacts via prop */}
      <style>{`/* sortByProgress controlled from toolbar: ${sortByProgress} */`}</style>
    </div>
  );
}
